import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getIncidentWorkflowLabel } from '../../../lib/incidentWorkflow';

const severityLabel = (s) => {
  const m = { low: 'Faible', medium: 'Moyenne', high: 'Haute', critical: 'Critique' };
  return m[s] || s || '—';
};

const categoryLabel = (c) => {
  const m = {
    quality: 'Problème de qualité',
    delivery: 'Problème de livraison',
    communication: 'Problème de communication',
    technical: 'Problème technique',
    other: 'Autre',
    delay: 'Retard',
    quality_issue: 'Problème de qualité',
    missing_info: 'Information manquante',
    technical_problem: 'Problème technique',
  };
  return m[c] || c || '—';
};

const statusBadgeClass = (status) => {
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200';
  if (status === 'in_progress') return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200';
  return 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100';
};

/**
 * Suivi lecture seule : statut + historique des messages (case_incident_activity) pour le dentiste.
 */
const DentistIncidentTrackingModal = ({ isOpen, onClose, incidentId, dentistUserId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);

  const load = useCallback(async () => {
    if (!incidentId || !dentistUserId) return;
    setLoading(true);
    setError(null);
    setDetail(null);
    setActivities([]);
    try {
      const { data: row, error: e1 } = await supabase
        .from('case_incidents')
        .select(
          `
          id,
          title,
          description,
          status,
          severity,
          incident_type,
          created_at,
          resolution_notes,
          cases!inner(id, case_number, user_profile_id, patient:patients(name))
        `,
        )
        .eq('id', incidentId)
        .maybeSingle();

      if (e1) throw e1;
      if (!row) {
        setError('Incident introuvable.');
        return;
      }
      if (row.cases?.user_profile_id !== dentistUserId) {
        setError('Vous ne pouvez pas consulter cet incident.');
        return;
      }

      const { data: actRows, error: e2 } = await supabase
        .from('case_incident_activity')
        .select(
          `
          id,
          from_status,
          to_status,
          message,
          created_at,
          actor:user_profiles!case_incident_activity_actor_id_fkey(full_name)
        `,
        )
        .eq('incident_id', incidentId)
        .order('created_at', { ascending: true });

      if (e2) throw e2;

      setDetail({
        title: row.title,
        description: row.description,
        status: row.status,
        severity: row.severity,
        incidentType: row.incident_type,
        createdAt: row.created_at,
        resolutionNotes: row.resolution_notes,
        caseId: row.cases.id,
        caseNumber: row.cases.case_number,
        patientName: row.cases.patient?.name || '—',
      });
      setActivities(
        (actRows || []).map((a) => ({
          id: a.id,
          fromStatus: a.from_status,
          toStatus: a.to_status,
          fromLabel: a.from_status ? getIncidentWorkflowLabel(a.from_status) : '—',
          toLabel: getIncidentWorkflowLabel(a.to_status),
          message: a.message,
          actorName: a.actor?.full_name || '—',
          dateLabel: a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : '—',
        })),
      );
    } catch (err) {
      console.error('[DentistIncidentTrackingModal]', err);
      setError(err?.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [incidentId, dentistUserId]);

  useEffect(() => {
    if (isOpen && incidentId && dentistUserId) {
      load();
    }
  }, [isOpen, incidentId, dentistUserId, load]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dentist-incident-tracking-title"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[min(90vh,720px)] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-border shrink-0">
          <div className="min-w-0">
            <h2 id="dentist-incident-tracking-title" className="text-lg font-semibold text-foreground truncate">
              Suivi de l’incident
            </h2>
            {detail && (
              <p className="text-sm text-muted-foreground mt-1">
                Conception #{detail.caseNumber} · {detail.patientName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground shrink-0"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Icon name="Loader" size={20} className="animate-spin text-primary" />
              Chargement…
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && detail && (
            <>
              <div className="flex flex-wrap gap-2 items-center">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(detail.status)}`}
                >
                  {getIncidentWorkflowLabel(detail.status)}
                </span>
                <span className="text-xs text-muted-foreground">
                  Signalé le{' '}
                  {detail.createdAt ? new Date(detail.createdAt).toLocaleDateString('fr-FR') : '—'}
                </span>
              </div>

              <div>
                <p className="text-base font-semibold text-foreground">{detail.title}</p>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <p>
                    <span className="text-muted-foreground">Gravité · </span>
                    <span className="text-foreground font-medium">{severityLabel(detail.severity)}</span>
                  </p>
                  <p>
                    <span className="text-muted-foreground">Catégorie · </span>
                    <span className="text-foreground font-medium">
                      {categoryLabel(detail.incidentType)}
                    </span>
                  </p>
                </div>
                {detail.description ? (
                  <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap border-t border-border pt-3">
                    {detail.description}
                  </p>
                ) : null}
              </div>

              <div>
                <h3 className="text-sm font-bold text-foreground uppercase tracking-wide flex items-center gap-2 mb-2">
                  <Icon name="MessageSquare" size={16} className="text-primary" />
                  Historique des échanges (équipe)
                </h3>
                {activities.length === 0 ? (
                  <p className="text-sm text-muted-foreground rounded-lg border border-dashed border-border p-4 bg-muted/20">
                    Aucune mise à jour enregistrée pour le moment. Les messages de l’équipe lors du traitement de
                    votre incident apparaîtront ici.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {activities.map((a) => (
                      <li key={a.id} className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
                        <p className="text-xs text-muted-foreground">
                          {a.dateLabel} · {a.actorName}
                        </p>
                        <p className="font-medium text-foreground mt-1">
                          {a.fromLabel} → {a.toLabel}
                        </p>
                        <p className="text-foreground mt-1 whitespace-pre-wrap">{a.message}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {detail.status === 'resolved' && detail.resolutionNotes ? (
                <div className="rounded-lg bg-success/10 border border-success/20 p-3 text-sm">
                  <p className="font-medium text-success mb-1">Clôture</p>
                  <p className="text-foreground whitespace-pre-wrap">{detail.resolutionNotes}</p>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 border-t border-border bg-muted/30 shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
          {!loading && !error && detail?.caseId && (
            <Button type="button" variant="default" asChild>
              <Link to={`/case-detail/${detail.caseId}?tab=incidents`} onClick={onClose}>
                Voir la fiche complète
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DentistIncidentTrackingModal;
